import { useMemo, type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form-latest';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { _isEmptyString, _notNil } from '@/littledash';
import { featuresSelector } from '@/support/Selectors';

interface ReasonForChangeProps {
  isDelete?: boolean;
  enableRules?: boolean;
  useErrorBoarder?: boolean;
}

const featureSelectorTwo = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
}));

const ReasonForChange: FC<ReasonForChangeProps> = ({ isDelete, enableRules = true, useErrorBoarder = true }) => {
  const { reasonForChange } = useSelector(featureSelectorTwo);

  const {
    formState: { errors },
    control,
  } = useFormContext();

  const rules = useMemo(() => {
    if (!enableRules) {
      return undefined;
    }
    const requiredMessage = `You must provide a reason for the ${isDelete === true ? 'deletion' : 'change'}`;
    return {
      validate: (value: unknown) => {
        const trimmed = typeof value === 'string' ? value.trim() : '';
        if (_isEmptyString(trimmed)) {
          return requiredMessage;
        }
        if (trimmed.length < 3) {
          return 'Please enter at least 3 characters';
        }
        if (trimmed.length > 255) {
          return 'Please enter less than 256 characters';
        }
        return true;
      },
    };
  }, [enableRules, isDelete]);

  return (
    <>
      {reasonForChange && (
        <div className="pt2" data-testid="reason-for-change-container">
          <label htmlFor="reason_for_change" data-testid="reason-for-label">
            Reason for {isDelete === true ? 'deletion' : 'change'} <span className="blue">*</span>
          </label>
          <Controller
            name="reason_for_change"
            control={control}
            rules={rules}
            render={({ field: { onChange, onBlur, value } }) => {
              const stringValue = typeof value === 'string' ? value : '';
              return (
                <textarea
                  value={stringValue}
                  onChange={(e) => onChange(e.target.value)}
                  onBlur={() => {
                    const trimmed = stringValue.trim();
                    if (trimmed !== stringValue) {
                      onChange(trimmed);
                    }
                    onBlur();
                  }}
                  id="reason_for_change"
                  style={{ resize: 'none' }}
                  className={cn('mb0', { input__error: _notNil(errors.reason_for_change) && useErrorBoarder })}
                  data-testid="reason-for-change-field"
                />
              );
            }}
          />
          <ErrorMessage
            errors={errors}
            name={`reason_for_change`}
            render={({ message }) => <p className="f6 red db lh-copy pt1">{message}</p>}
          />
        </div>
      )}
    </>
  );
};

export default ReasonForChange;
